import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import {
  AppBar,
  Toolbar,
  Typography,
  Stack,
  IconButton,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  Box,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { IoChevronBack, IoChevronForward, IoArrowBack, IoSchoolOutline } from 'react-icons/io5';
import useResponsive from '../../hooks/useResponsive';
import AccountPopover from './AccountPopover';
import { HamBurger } from 'src/images/svg';
import { useAuth } from '../../context/auth/AuthContext';
import { getRoleLabel } from 'src/config/permissions';
import { useHeader } from 'src/context/header/HeaderContext';
import palette from 'src/theme/palette';

const APPBAR_MOBILE = 50;
const APPBAR_DESKTOP = 56;
const DRAWER_WIDTH = 240;

const RootStyle = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none',
  backgroundColor: '#FFFFFF',
  color: '#0F172A',
  borderBottom: '1px solid #E2E8F0',
  position: 'fixed',
  top: 0,
  right: 0,
  zIndex: theme.zIndex.appBar,
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
  },
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: `${APPBAR_MOBILE}px !important`,
  height: APPBAR_MOBILE,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('lg')]: {
    minHeight: `${APPBAR_DESKTOP}px !important`,
    height: APPBAR_DESKTOP,
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(3),
  },
}));

interface DashboardNavbarProps {
  onOpenSidebar?: () => void;
}

interface BreadcrumbItem {
  label: string;
  path?: string;
  isPill?: boolean;
  pillColor?: string;
  icon?: any;
}

interface NavTreeResult {
  backUrl?: string;
  backLabel?: string;
  breadcrumbs: BreadcrumbItem[];
  title: string;
}

function resolveNavigationTree(pathname: string, userRole?: string): NavTreeResult {
  const isAdmin = pathname.startsWith('/admin');
  const isTeacher = pathname.startsWith('/teacher');
  const isStudent = pathname.startsWith('/student');
  const prefix = isAdmin ? '/admin' : isTeacher ? '/teacher' : '/student';

  // 1. Dashboard
  if (pathname === '/admin/dashboard' || pathname === '/teacher/dashboard' || pathname === '/student/dashboard') {
    return {
      title: 'Dashboard',
      breadcrumbs: [{ label: 'Dashboard' }],
    };
  }

  // 2. Cohorts
  if (pathname === '/admin/cohorts') {
    return {
      title: 'Cohorts Hub',
      breadcrumbs: [{ label: 'Cohorts Hub' }],
    };
  }
  if (pathname.startsWith('/admin/cohorts/')) {
    return {
      backUrl: '/admin/cohorts',
      backLabel: 'Cohorts',
      title: 'Cohort Details',
      breadcrumbs: [
        { label: 'Cohorts Hub', path: '/admin/cohorts' },
        { label: 'Cohort Details' },
      ],
    };
  }

  // Roles
  if (pathname === '/admin/roles') {
    return {
      title: 'Roles & Permissions',
      breadcrumbs: [{ label: 'Roles & Permissions' }],
    };
  }
  if (pathname.startsWith('/admin/roles/')) {
    return {
      backUrl: '/admin/roles',
      backLabel: 'Roles',
      title: 'Role Workspace',
      breadcrumbs: [{ label: 'Roles & Permissions', path: '/admin/roles' }, { label: 'Role Workspace' }],
    };
  }

  // 3. Students
  if (pathname === `${prefix}/students`) {
    return {
      title: 'Students Directory',
      breadcrumbs: [{ label: 'Students' }],
    };
  }
  if (pathname.includes('/view-student/')) {
    return {
      backUrl: `${prefix}/students`,
      backLabel: 'Students',
      title: 'Student Profile',
      breadcrumbs: [
        { label: 'Students Directory', path: `${prefix}/students` },
        { label: 'Student Profile' },
      ],
    };
  }
  if (pathname.includes('/edit-student')) {
    return {
      backUrl: `${prefix}/students`,
      backLabel: 'Students',
      title: 'Edit Student',
      breadcrumbs: [
        { label: 'Students Directory', path: `${prefix}/students` },
        { label: 'Edit Student' },
      ],
    };
  }
  if (pathname === `${prefix}/add-student` || pathname === `${prefix}/add-student/`) {
    return {
      backUrl: `${prefix}/students`,
      backLabel: 'Students',
      title: 'Add New Student',
      breadcrumbs: [
        { label: 'Students Directory', path: `${prefix}/students` },
        { label: 'Add Student' },
      ],
    };
  }

  // 4. Teachers
  if (pathname === '/admin/teachers') {
    return {
      title: 'Teachers Directory',
      breadcrumbs: [{ label: 'Teachers' }],
    };
  }
  if (pathname.startsWith('/admin/view-teacher/')) {
    return {
      backUrl: '/admin/teachers',
      backLabel: 'Teachers',
      title: 'Teacher Profile',
      breadcrumbs: [
        { label: 'Teachers Directory', path: '/admin/teachers' },
        { label: 'Teacher Profile' },
      ],
    };
  }
  if (pathname.startsWith('/admin/edit-teacher/')) {
    return {
      backUrl: '/admin/teachers',
      backLabel: 'Teachers',
      title: 'Edit Teacher',
      breadcrumbs: [
        { label: 'Teachers Directory', path: '/admin/teachers' },
        { label: 'Edit Teacher' },
      ],
    };
  }
  if (pathname === '/admin/add-teacher' || pathname === '/admin/add-new-teacher') {
    return {
      backUrl: '/admin/teachers',
      backLabel: 'Teachers',
      title: 'Add New Teacher',
      breadcrumbs: [
        { label: 'Teachers Directory', path: '/admin/teachers' },
        { label: 'Add Teacher' },
      ],
    };
  }

  // 5. Scholar Dollar Pay (Payroll)
  if (pathname === '/admin/payroll') {
    return {
      title: 'Scholar Dollar Pay',
      breadcrumbs: [{ label: 'Scholar Dollar Pay' }],
    };
  }
  if (pathname === '/admin/payroll/import') {
    return {
      backUrl: '/admin/payroll',
      backLabel: 'Scholar Dollar Pay',
      title: 'Import Payroll CSV',
      breadcrumbs: [
        { label: 'Scholar Dollar Pay', path: '/admin/payroll' },
        { label: 'Import Payroll' },
      ],
    };
  }
  if (pathname.startsWith('/admin/payroll/imports/')) {
    return {
      backUrl: '/admin/payroll',
      backLabel: 'Scholar Dollar Pay',
      title: 'Payroll Batch Details',
      breadcrumbs: [
        { label: 'Scholar Dollar Pay', path: '/admin/payroll' },
        { label: 'Batch Details' },
      ],
    };
  }
  if (pathname === '/admin/payroll/roster') {
    return {
      backUrl: '/admin/payroll',
      backLabel: 'Scholar Dollar Pay',
      title: 'Student Account Setup',
      breadcrumbs: [
        { label: 'Scholar Dollar Pay', path: '/admin/payroll' },
        { label: 'Student Accounts' },
      ],
    };
  }
  if (pathname === '/admin/payroll/tax-day') {
    return {
      backUrl: '/admin/payroll',
      backLabel: 'Scholar Dollar Pay',
      title: 'Tax Day Export',
      breadcrumbs: [
        { label: 'Scholar Dollar Pay', path: '/admin/payroll' },
        { label: 'Tax Day Export' },
      ],
    };
  }

  // 6. Transactions & Status Tracker
  if (pathname === '/admin/transaction-status-tracker') {
    return {
      title: 'Transactions Tracker',
      breadcrumbs: [{ label: 'Transactions Tracker' }],
    };
  }
  if (pathname === '/teacher/transaction-management') {
    return {
      title: 'Transaction Management',
      breadcrumbs: [{ label: 'Transactions' }],
    };
  }
  if (pathname === '/admin/manage-transaction-types') {
    return {
      title: 'Transaction Types',
      breadcrumbs: [{ label: 'Transaction Types' }],
    };
  }

  // 7. Loans
  if (pathname === `${prefix}/loan-requests`) {
    return {
      title: 'Loan Requests',
      breadcrumbs: [{ label: 'Loan Requests' }],
    };
  }
  if (
    pathname.includes('/view-loan/') ||
    (pathname.includes('/new-loan-request/') && pathname.split('/').length > 3) ||
    (pathname.startsWith(`${prefix}/loan-requests/`) && pathname !== `${prefix}/loan-requests`)
  ) {
    return {
      backUrl: `${prefix}/loan-requests`,
      backLabel: 'Loan Requests',
      title: 'Loan Details',
      breadcrumbs: [
        { label: 'Loan Requests', path: `${prefix}/loan-requests` },
        { label: 'Loan Details' },
      ],
    };
  }
  if (
    pathname === `${prefix}/new-loan-request` ||
    pathname === '/student/new-loan-request' ||
    pathname === '/admin/new-loan-request'
  ) {
    return {
      backUrl: `${prefix}/loan-requests`,
      backLabel: 'Loan Requests',
      title: 'New Loan Application',
      breadcrumbs: [
        { label: 'Loan Requests', path: `${prefix}/loan-requests` },
        { label: 'Apply for Loan' },
      ],
    };
  }

  // 8. Classes
  if (pathname === `${prefix}/classes`) {
    return {
      title: 'Classes Directory',
      breadcrumbs: [{ label: 'Classes' }],
    };
  }
  if (pathname.includes('/add-new-class')) {
    return {
      backUrl: `${prefix}/classes`,
      backLabel: 'Classes',
      title: 'Create Class',
      breadcrumbs: [
        { label: 'Classes', path: `${prefix}/classes` },
        { label: 'New Class' },
      ],
    };
  }

  // 9. Badges
  if (pathname === `${prefix}/badges`) {
    return {
      title: 'Achievement Badges',
      breadcrumbs: [{ label: 'Badges' }],
    };
  }
  if (pathname.includes('/add-new-badge')) {
    return {
      backUrl: `${prefix}/badges`,
      backLabel: 'Badges',
      title: 'Create Badge',
      breadcrumbs: [
        { label: 'Badges', path: `${prefix}/badges` },
        { label: 'New Badge' },
      ],
    };
  }
  if (pathname.includes('/edit-badge/')) {
    return {
      backUrl: `${prefix}/badges`,
      backLabel: 'Badges',
      title: 'Edit Badge',
      breadcrumbs: [
        { label: 'Badges', path: `${prefix}/badges` },
        { label: 'Edit Badge' },
      ],
    };
  }

  // 10. S.T.R.E.E.T Cred
  if (pathname === `${prefix}/street-cred`) {
    return {
      title: 'S.T.R.E.E.T Cred',
      breadcrumbs: [{ label: 'S.T.R.E.E.T Cred' }],
    };
  }
  if (pathname.includes('/add-behavior-type')) {
    return {
      backUrl: `${prefix}/street-cred`,
      backLabel: 'S.T.R.E.E.T Cred',
      title: 'Add Behavior Type',
      breadcrumbs: [
        { label: 'S.T.R.E.E.T Cred', path: `${prefix}/street-cred` },
        { label: 'Add Behavior Type' },
      ],
    };
  }
  if (pathname.includes('/edit-behaviour-type') || pathname.includes('/edit-behavior-type')) {
    return {
      backUrl: `${prefix}/street-cred`,
      backLabel: 'S.T.R.E.E.T Cred',
      title: 'Edit Behavior Type',
      breadcrumbs: [
        { label: 'S.T.R.E.E.T Cred', path: `${prefix}/street-cred` },
        { label: 'Edit Behavior Type' },
      ],
    };
  }
  if (pathname.includes('/add-student-behaviour') || pathname.includes('/edit-student-behavior')) {
    return {
      backUrl: `${prefix}/street-cred`,
      backLabel: 'S.T.R.E.E.T Cred',
      title: 'Log Scholar Behavior',
      breadcrumbs: [
        { label: 'S.T.R.E.E.T Cred', path: `${prefix}/street-cred` },
        { label: 'Log Behavior' },
      ],
    };
  }

  // 11. Whammies (Financial Simulations)
  if (pathname === `${prefix}/financial-simulations`) {
    return {
      title: 'Whammies',
      breadcrumbs: [{ label: 'Whammies' }],
    };
  }
  if (pathname.includes('/view-financial-simulations/')) {
    return {
      backUrl: `${prefix}/financial-simulations`,
      backLabel: 'Whammies',
      title: 'Whammy Challenge Details',
      breadcrumbs: [
        { label: 'Whammies', path: `${prefix}/financial-simulations` },
        { label: 'Challenge Details' },
      ],
    };
  }
  if (pathname.includes('/add-scenario')) {
    return {
      backUrl: `${prefix}/financial-simulations`,
      backLabel: 'Whammies',
      title: 'Create Whammy Scenario',
      breadcrumbs: [
        { label: 'Whammies', path: `${prefix}/financial-simulations` },
        { label: 'Create Scenario' },
      ],
    };
  }
  if (pathname.includes('/edit-scenario/')) {
    return {
      backUrl: `${prefix}/financial-simulations`,
      backLabel: 'Whammies',
      title: 'Edit Whammy Scenario',
      breadcrumbs: [
        { label: 'Whammies', path: `${prefix}/financial-simulations` },
        { label: 'Edit Scenario' },
      ],
    };
  }
  if (pathname.includes('/assign-new-chellenge')) {
    return {
      backUrl: `${prefix}/financial-simulations`,
      backLabel: 'Whammies',
      title: 'Assign Whammy Challenge',
      breadcrumbs: [
        { label: 'Whammies', path: `${prefix}/financial-simulations` },
        { label: 'Assign Whammy' },
      ],
    };
  }

  // 12. Student Specifics
  if (pathname === '/student/paychecks') {
    return {
      title: 'Paychecks & Earnings',
      breadcrumbs: [{ label: 'Paychecks' }],
    };
  }
  if (pathname === '/student/bills') {
    return {
      title: 'Pay Bills',
      breadcrumbs: [{ label: 'Pay Bills' }],
    };
  }
  if (pathname === '/student/financial-tools') {
    return {
      title: 'Financial Tools',
      breadcrumbs: [{ label: 'Financial Tools' }],
    };
  }
  if (pathname === `${prefix}/street-cents`) {
    return {
      title: 'S.T.R.E.E.T Cents',
      breadcrumbs: [{ label: 'S.T.R.E.E.T Cents' }],
    };
  }
  if (pathname.startsWith(`${prefix}/street-cents/`) || pathname.startsWith(`${prefix}/view-transaction/`)) {
    return {
      backUrl: `${prefix}/street-cents`,
      backLabel: 'S.T.R.E.E.T Cents',
      title: 'Transaction Receipt',
      breadcrumbs: [
        { label: 'S.T.R.E.E.T Cents', path: `${prefix}/street-cents` },
        { label: 'Transaction Receipt' },
      ],
    };
  }

  // 13. Settings
  if (pathname.includes('/edit-profile')) {
    return {
      backUrl: `${prefix}/dashboard`,
      backLabel: 'Dashboard',
      title: 'Profile Settings',
      breadcrumbs: [
        { label: 'Dashboard', path: `${prefix}/dashboard` },
        { label: 'Profile Settings' },
      ],
    };
  }
  if (pathname.includes('/change-password')) {
    return {
      backUrl: `${prefix}/edit-profile`,
      backLabel: 'Profile Settings',
      title: 'Change Password',
      breadcrumbs: [
        { label: 'Profile Settings', path: `${prefix}/edit-profile` },
        { label: 'Change Password' },
      ],
    };
  }

  // Fallback parsed title
  const segments = pathname.split('/').filter(Boolean);
  const rawTitle = segments[segments.length - 1] || 'Dashboard';
  const cleanTitle = rawTitle.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return {
    title: cleanTitle,
    breadcrumbs: [{ label: cleanTitle }],
  };
}

export default function DashboardNavbar({ onOpenSidebar }: DashboardNavbarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useResponsive('up', 'lg');
  let user = null;
  try {
    const auth = useAuth();
    user = auth?.user;
  } catch (e) {
    user = null;
  }
  const headerContext = useHeader();
  const headerState = headerContext?.headerState;

  const defaultNavInfo = useMemo(() => {
    return resolveNavigationTree(location.pathname, user?.role);
  }, [location.pathname, user?.role]);

  // Contextual overrides from active child page
  const breadcrumbs = headerState?.breadcrumbs || defaultNavInfo.breadcrumbs;
  const currentTitle = headerState?.customTitle || defaultNavInfo.title;
  const backUrl = headerState?.backUrl !== undefined ? headerState.backUrl : defaultNavInfo.backUrl;
  const backLabel = headerState?.backLabel !== undefined ? headerState.backLabel : defaultNavInfo.backLabel;

  const roleLabel = getRoleLabel(user?.role);

  const hasBack = Boolean(backUrl || backLabel);

  return (
    <RootStyle>
      <ToolbarStyle>
        {/* Left Section: Mobile Menu Toggle & Navigation Tree */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
          {!isDesktop && (
            <IconButton
              size="small"
              aria-label="Open sidebar"
              onClick={onOpenSidebar}
              sx={{
                p: 0.75,
                mr: 0.5,
                color: '#475569',
                borderRadius: 1.5,
                '&:hover': { bgcolor: '#F1F5F9' },
              }}
            >
              <HamBurger />
            </IconButton>
          )}

          {hasBack && backLabel && (
            <Button
              size="small"
              variant="outlined"
              aria-label="Back"
              startIcon={<IoArrowBack size={13} />}
              onClick={() => (backUrl ? navigate(backUrl) : navigate(-1))}
              sx={{
                color: '#334155',
                bgcolor: '#FFFFFF',
                borderColor: '#E2E8F0',
                fontWeight: 700,
                px: 1,
                py: 0.25,
                height: 28,
                borderRadius: 1.5,
                fontSize: '0.725rem',
                textTransform: 'none',
                mr: 0.75,
                flexShrink: 0,
                '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E0' },
              }}
            >
              {backLabel}
            </Button>
          )}

          {hasBack && !backLabel && (
            <IconButton
              size="small"
              aria-label="Back"
              onClick={() => (backUrl ? navigate(backUrl) : navigate(-1))}
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.5,
                border: '1px solid #E2E8F0',
                bgcolor: '#F8FAFC',
                color: '#475569',
                p: 0,
                mr: 0.5,
                flexShrink: 0,
                '&:hover': { bgcolor: '#F1F5F9', color: '#0F172A' },
              }}
            >
              <IoChevronBack size={14} />
            </IconButton>
          )}

          {breadcrumbs.length > 1 ? (
            <Breadcrumbs
              separator={<IoChevronForward size={11} color="#94A3B8" />}
              aria-label="breadcrumb"
              sx={{
                '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap', alignItems: 'center' },
                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              }}
            >
              {breadcrumbs.map((item, idx) => {
                const isLast = idx === breadcrumbs.length - 1;
                if (isLast || !item.path) {
                  return (
                    <Typography
                      key={(item.label || '') + idx}
                      noWrap
                      sx={{
                        color: '#0F172A',
                        fontWeight: 800,
                        fontSize: { xs: '0.78rem', sm: '0.84rem' },
                        letterSpacing: -0.2,
                        px: 0.25,
                      }}
                    >
                      {item.label}
                    </Typography>
                  );
                }

                if (item.isPill) {
                  return (
                    <Link
                      key={(item.label || '') + idx}
                      component={RouterLink}
                      to={item.path}
                      underline="none"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: '#1D4ED8',
                        bgcolor: '#EFF6FF',
                        border: '1px solid #DBEAFE',
                        fontWeight: 700,
                        fontSize: '0.725rem',
                        px: 0.9,
                        py: 0.15,
                        borderRadius: 1.25,
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: '#DBEAFE', color: '#1E40AF' },
                      }}
                    >
                      {item.label}
                    </Link>
                  );
                }

                return (
                  <Link
                    key={(item.label || '') + idx}
                    component={RouterLink}
                    to={item.path}
                    underline="hover"
                    noWrap
                    sx={{
                      color: '#64748B',
                      fontWeight: 600,
                      fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 0.5,
                      py: 0.2,
                      borderRadius: 1,
                      transition: 'all 0.15s ease',
                      '&:hover': { color: '#0F172A', bgcolor: '#F1F5F9' },
                    }}
                  >
                    {item.icon || (idx === 0 ? <IoSchoolOutline size={13} color="#64748B" /> : null)}
                    {item.label}
                  </Link>
                );
              })}
            </Breadcrumbs>
          ) : (
            <Typography
              noWrap
              variant="subtitle1"
              sx={{
                color: '#0F172A',
                fontWeight: 800,
                fontSize: { xs: '0.875rem', sm: '0.95rem' },
                letterSpacing: -0.2,
              }}
            >
              {currentTitle}
            </Typography>
          )}
        </Stack>

        {/* Right Section: Role Badge & Account Popover */}
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexShrink: 0, ml: 1 }}>
          {roleLabel && (
            <Chip
              label={roleLabel}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.675rem',
                fontWeight: 700,
                bgcolor: '#F1F5F9',
                color: '#475569',
                borderRadius: 1,
                border: '1px solid #E2E8F0',
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            />
          )}

          <AccountPopover />
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  );
}
